import { createSelector } from "reselect";
import type { RootState } from "typesafe-actions";
import {
  PROJECT_ADMIN_ID,
  PROJECT_CONTRIBUTOR_ID,
  PROJECT_VIEWER_ID,
} from "../../constants/projectRoles.constant";
import { selectCanUseProjects } from "../config/config.selector";
import type {
  Project,
  ProjectPermissions,
  ProjectResponse,
  ProjectRole,
  ProjectWithUserRole,
  WorkspaceProjects,
} from "./types";
import type { WorkspacesState } from "./workspaces.reducer";

const selectWorkspaces = (state: RootState): WorkspacesState =>
  state.context.workspaces;

const getAssignableProjectsFromState = (state: RootState) =>
  state.context.workspaces.assignableProjects;

// TODO: Move this business logic to service: Requires Service endpoint that returns permissions instead of roles (or an endpoint to map roles -> permissions)
export const ProjectAdminRoleId = "D9F8F8E9-E9E0-4D4D-B8E8-D9F8F8E9E0D5";
export const UNKNOWN_PROJECT_LABEL = "Unknown";

function isProjectResponseWithRole(
  project: ProjectResponse,
): project is ProjectWithUserRole {
  // Looks somewhat stupid to "as cast" project to type we are checking for, but otherwise TS will complain about "userRole" field not being defined.
  return typeof (project as ProjectWithUserRole).userRole?.roleId === "string";
}

function toProjectRole(
  projectResponse: ProjectResponse,
): ProjectRole | undefined {
  if (!isProjectResponseWithRole(projectResponse)) return undefined;
  switch (projectResponse.userRole.roleId.toLowerCase()) {
    case PROJECT_ADMIN_ID.toLowerCase():
      return "admin";
    case PROJECT_CONTRIBUTOR_ID.toLowerCase():
      return "contributor";
    case PROJECT_VIEWER_ID.toLowerCase():
      return "viewer";
    default:
      return undefined;
  }
}

const getProjectPermissions = (
  projectResponse: ProjectResponse,
): ProjectPermissions[] => {
  if (!isProjectResponseWithRole(projectResponse)) return [];
  // admin role
  if (projectResponse.userRole.roleId.toUpperCase() === ProjectAdminRoleId)
    return ["AddProjectWorkspaces", "RemoveProjectWorkspaces"];
  // any other role (we do not have any other roles that we need to derived permissions for yet)
  return [];
};

const toProject = (projectResponse: ProjectResponse): Project => ({
  autoSubscribe: projectResponse.autoSubscribe,
  id: projectResponse.id,
  name: projectResponse.name,
  description: projectResponse.description,
  color: projectResponse.appearanceProperties?.color ?? "",
  icon: projectResponse.appearanceProperties?.icon || "box",
  userPermissions: getProjectPermissions(projectResponse),
  hasAdmin: !!(
    isProjectResponseWithRole(projectResponse) &&
    projectResponse.userRole.roleId.toUpperCase() === ProjectAdminRoleId
  ),
  projectRole: toProjectRole(projectResponse),
  status: projectResponse.status,
  endDate: projectResponse.endDate,
});

// Maps the project response to a Project object and sets the "hasAdmin" property to true.
// Assignable projects are those where the current user has an admin role.
const toAssignableProject = (projectResponse: ProjectResponse): Project => {
  return {
    ...toProject(projectResponse),
    hasAdmin: true,
  };
};

export const getAssignableProjects = createSelector(
  [getAssignableProjectsFromState],
  (assignableProjects): WorkspaceProjects => {
    if (!assignableProjects) return [];

    const allProjects = assignableProjects.map((ap) => toAssignableProject(ap));
    allProjects.sort((a, b) => a.name.localeCompare(b.name)); // alphabetically sorted
    return allProjects;
  },
);

const selectWorkspacesProjects = createSelector(
  [selectWorkspaces],
  (workspaces) => workspaces.projects,
);

export const selectDashboardSelectedProject = createSelector(
  [selectWorkspaces],
  (state) => state.selectedProject,
);

export const getSelectedProjectColor = createSelector(
  [selectDashboardSelectedProject],
  (project): string | undefined => project?.color,
);

export const getAllProjectCollaborators = (state: RootState) =>
  state.context.workspaces.projectCollaborators;

export const getProjectFromId = createSelector(
  [selectWorkspacesProjects, (_state: RootState, id: string | undefined) => id],
  (projects, id) => {
    if (!projects || !id) return undefined;
    const project = projects.get(id);
    if (!project) return undefined;
    return toProject(project);
  },
);

export const createSelectProjectByIdFillingUnknownOne = () =>
  createSelector(
    [
      selectDashboardSelectedProject,
      selectWorkspacesProjects,
      (_state: RootState, id: string | null | undefined) => id,
    ],
    (selectedProject, projects, id): Project | undefined => {
      if (!id) return undefined;
      if (selectedProject && !projects) return undefined; // project selected but projects are not loaded

      const project = projects?.get(id);
      if (!project)
        return {
          autoSubscribe: null,
          color: "red",
          endDate: null,
          hasAdmin: false,
          icon: "box",
          id,
          name: UNKNOWN_PROJECT_LABEL,
          status: "Active",
          userPermissions: [],
        };
      return toProject(project);
    },
  );

export const createSelectCanRemoveProjectFromWorkspace = () =>
  createSelector(
    createSelectProjectByIdFillingUnknownOne(),
    selectCanUseProjects,
    (project, canUseProjects) => {
      return (
        canUseProjects &&
        !!project?.userPermissions.includes("RemoveProjectWorkspaces")
      );
    },
  );

export const getAllProjects = createSelector(
  [selectWorkspacesProjects],
  (projects): WorkspaceProjects | undefined => {
    if (!projects) return undefined;

    const allProjects = [...projects.values()].map((ap) => toProject(ap));
    allProjects.sort((a, b) => a.name.localeCompare(b.name)); // alphabetically sorted
    return allProjects;
  },
);

export const selectProjectById = createSelector(
  [getAllProjects, (_: RootState, projectId?: string) => projectId],
  (projects, projectId) =>
    projects?.find((project) => project.id === projectId),
);

export const isProjectInactive = createSelector(
  [getProjectFromId],
  (project) => {
    if (!project) return false;
    return project.status === "Inactive";
  },
);

export const isAdminOfSelectedProject = createSelector(
  [selectDashboardSelectedProject],
  (selectedProject) => !!selectedProject?.hasAdmin,
);
